import type { WorldFeature, FeatureOutput, BuildContext } from '../types';
import { createGroundElevation } from '../../site/groundElevation';
import { WORLD } from '../../core/config';
import { loadFieldClasses } from '../../data/loadFieldClasses';
import { loadWaterClasses } from '../../data/loadWaterClasses';
import { bodyClassesOf } from '../../data/waterClasses';
import { createStyleParams } from '../../render/style';
import { buildShoreGrid } from '../../render/shoreGrid';
import { buildGroundMeshFromPieces, createGroundMeshAccumulator } from './groundMesh';
import { groundChunkJobs } from './groundChunks';
import { groundWithoutDecks } from './groundDecks';
import { createStreetRelief } from '../../site/streetRelief';
import { waterAccesses } from '../../site/waterAccess';
import { buildFarRingLandMesh } from './farRing';
import { buildWaterMesh } from './waterMesh';
import { buildQuays } from './quays';
import { buildAccessStairs } from './accessStairs';
import { buildBridges } from './bridges';
import { createDensifyConfig } from './densify';
import type { FieldRegionClasses } from './groundStyle';
import type { WaterBodyClasses } from './waterKinds';
import { damWaterCovers } from '../fabric/landmarks/damWaterCover';
import { createWaterClassSampler } from './waterKinds';
import { classifyWaterAreas } from './waterAreas';

// Classes that do not fit the layout would colour the wrong fields, so every piece falls back to its
// kind's colour and the build says why.
function fieldClassesFor(ctx: BuildContext): FieldRegionClasses {
  const loaded = loadFieldClasses(ctx.layout);
  if (loaded.ok) return loaded.value.regions;
  ctx.warn(`[terrain] Field classes do not fit the layout: ${loaded.error.slice(0, 3).join('; ')}`);
  return {};
}

// Classes that do not fit the layout could colour the wrong water, so every body takes its kind's
// class and the build says why.
function waterClassesFor(ctx: BuildContext): WaterBodyClasses {
  const loaded = loadWaterClasses(ctx.layout);
  if (loaded.ok) return bodyClassesOf(loaded.value);
  ctx.warn(`[terrain] Water classes do not fit the layout: ${loaded.error.slice(0, 3).join('; ')}`);
  return {};
}

const groundFeature: WorldFeature = {
  id: 'terrain:ground',
  label: 'Ground',
  weight: 4,
  batches(ctx: BuildContext) {
    const densify = {
      ...createDensifyConfig(ctx.painterView, WORLD.groundY),
      triangulations: ctx.terrainPlans?.ground,
    };
    const fields = fieldClassesFor(ctx);
    const relief = createStreetRelief(ctx.layout, { damDeck: false });
    const jobs = groundChunkJobs(
      groundWithoutDecks(ctx.paving.ground, ctx.layout),
      ctx.site.bounds
    );
    const size = 16;
    return jobs.flatMap((job, i) => {
      let mesh: ReturnType<typeof createGroundMeshAccumulator> | undefined;
      const count = Math.ceil(job.pieceCount / size);
      return Array.from({ length: count }, (_, batch) => ({
        label: `Ground chunk ${i + 1}/${jobs.length} batch ${batch + 1}/${count}`,
        build: (): FeatureOutput => {
          mesh ??= createGroundMeshAccumulator(
            ctx.palette,
            ctx.materials,
            fields,
            densify,
            relief,
            createGroundElevation(ctx.layout)
          );
          mesh.append(job.pieces(batch * size, (batch + 1) * size));
          return {
            objects: batch === count - 1 ? [mesh.finish()] : [],
            obstacles: [],
            animated: [],
            counts: { groundPieces: i === 0 && batch === 0 ? ctx.site.ground.length : 0 },
          };
        },
      }));
    });
  },
  build(ctx: BuildContext): FeatureOutput {
    const densifyConfig = createDensifyConfig(ctx.painterView, WORLD.groundY);
    // The paving draws the streets it paves itself, so the ground draws only what it leaves.
    const mesh = buildGroundMeshFromPieces(
      groundWithoutDecks(ctx.paving.ground, ctx.layout),
      ctx.palette,
      ctx.materials,
      densifyConfig,
      fieldClassesFor(ctx),
      createStreetRelief(ctx.layout, { damDeck: false }),
      createGroundElevation(ctx.layout)
    );
    return {
      objects: [mesh],
      obstacles: [],
      animated: [],
      counts: { groundPieces: ctx.site.ground.length },
    };
  },
};

const farRingFeature: WorldFeature = {
  id: 'terrain:farRing',
  label: 'Far ring',
  weight: 2,
  build(ctx: BuildContext): FeatureOutput {
    const densifyConfig = createDensifyConfig(ctx.painterView, WORLD.groundY);
    const seam = {
      fields: fieldClassesFor(ctx),
      atmosphere: createStyleParams(ctx.palette, ctx.painterView).atmosphere,
    };
    const mesh = buildFarRingLandMesh(ctx.site, ctx.palette, ctx.materials, seam, densifyConfig);
    return {
      objects: [mesh],
      obstacles: [],
      animated: [],
      counts: {
        farRingLandPolygons: ctx.site.farRing.land.length,
        farRingWaterPolygons: ctx.site.farRing.water.length,
      },
    };
  },
};

const waterFeature: WorldFeature = {
  id: 'terrain:water',
  label: 'Water',
  weight: 3,
  build(ctx: BuildContext): FeatureOutput {
    const densifyConfig = {
      ...createDensifyConfig(ctx.painterView, WORLD.waterY),
      triangulations: ctx.terrainPlans?.water?.triangles,
    };
    const bodies = waterClassesFor(ctx);
    const sampler =
      ctx.terrainPlans?.water &&
      createWaterClassSampler(
        ctx.site.queries,
        classifyWaterAreas(ctx.layout.water.areas, ctx.layout.land.cityEnclosure),
        bodies,
        ctx.terrainPlans.water.labels
      );
    const mesh = buildWaterMesh(
      ctx.site,
      ctx.layout,
      ctx.palette,
      ctx.materials,
      densifyConfig,
      bodies,
      sampler
    );
    ctx.materials.setShoreGrid(buildShoreGrid(ctx.site.waterEdges.map((edge) => edge.segment)));
    ctx.materials.setWaterCovers(damWaterCovers(ctx.layout.landmarks, ctx.palette));
    return {
      objects: [mesh],
      obstacles: [],
      animated: [],
      counts: { waterPolygons: ctx.site.water.length },
    };
  },
};

const quaysFeature: WorldFeature = {
  id: 'terrain:quays',
  label: 'Quays',
  weight: 2,
  build(ctx: BuildContext): FeatureOutput {
    const { count } = buildQuays(
      ctx.site.waterEdges,
      ctx.palette,
      ctx.chunks,
      waterAccesses(ctx.layout),
      createGroundElevation(ctx.layout)
    );
    return {
      objects: [],
      obstacles: [],
      animated: [],
      counts: { quays: count },
    };
  },
};

const bridgesFeature: WorldFeature = {
  id: 'terrain:bridges',
  label: 'Bridges',
  weight: 1,
  build(ctx: BuildContext): FeatureOutput {
    const { count } = buildBridges(ctx.site.bridges, ctx.palette, ctx.chunks);
    const waterStairs = buildAccessStairs(ctx.layout, ctx.palette, ctx.chunks);
    return {
      objects: [],
      obstacles: [],
      animated: [],
      counts: { bridges: count, waterStairs },
    };
  },
};

export const TERRAIN_FEATURES: readonly WorldFeature[] = [
  groundFeature,
  farRingFeature,
  waterFeature,
  quaysFeature,
  bridgesFeature,
];
